import { useEffect, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { listExerciseModes, listSlotTemplates } from "@/api/catalog";
import { listOrganizations } from "@/api/organizations";
import { listPractitioners } from "@/api/practitioners";
import {
  type ShiftPattern,
  createShiftPattern,
  deleteShiftPattern,
  listShiftPatterns,
} from "@/api/shift_patterns";
import { PageHeader } from "@/components/PageHeader";

const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7] as const;

export function ShiftPatternsAdminPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const [orgId, setOrgId] = useState<string>("");
  const [open, setOpen] = useState(false);
  const [practitionerId, setPractitionerId] = useState<string>("");
  const [slotId, setSlotId] = useState<string>("");
  const [modeId, setModeId] = useState<string>("");
  const [weekday, setWeekday] = useState<number>(1);
  const [effectiveFrom, setEffectiveFrom] = useState<string>("");
  const [effectiveTo, setEffectiveTo] = useState<string>("");
  const [name, setName] = useState<string>("");
  const [formError, setFormError] = useState<string | null>(null);

  const orgs = useQuery({
    queryKey: ["organizations-active"],
    queryFn: () => listOrganizations({ status: "ACTIVE", limit: 200 }),
  });

  useEffect(() => {
    const first = orgs.data?.items[0];
    if (!orgId && first) {
      setOrgId(first.id);
    }
  }, [orgs.data, orgId]);

  const slots = useQuery({
    queryKey: ["slot-templates", orgId],
    queryFn: () => listSlotTemplates(orgId || undefined),
    enabled: Boolean(orgId),
  });

  const modes = useQuery({
    queryKey: ["exercise-modes"],
    queryFn: () => listExerciseModes(),
  });

  const practitioners = useQuery({
    queryKey: ["practitioners-all"],
    queryFn: () => listPractitioners({ limit: 200, status: "ACTIVE" }),
  });

  const patterns = useQuery({
    queryKey: ["shift-patterns", orgId],
    queryFn: () =>
      listShiftPatterns({ organization_id: orgId, active_only: false }),
    enabled: Boolean(orgId),
  });

  const resetForm = () => {
    setPractitionerId("");
    setSlotId("");
    setModeId("");
    setWeekday(1);
    setEffectiveFrom("");
    setEffectiveTo("");
    setName("");
    setFormError(null);
  };

  const createMutation = useMutation({
    mutationFn: () =>
      createShiftPattern({
        organization_id: orgId,
        practitioner_id: practitionerId,
        slot_template_id: slotId,
        exercise_mode_id: modeId,
        weekday,
        effective_from: effectiveFrom,
        effective_to: effectiveTo || null,
        name: name || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["shift-patterns"] });
      setOpen(false);
      resetForm();
    },
    onError: (e: unknown) => {
      const detail = (e as { response?: { data?: { detail?: string } } }).response?.data?.detail;
      setFormError(detail ?? (e as Error).message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteShiftPattern(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["shift-patterns"] }),
  });

  const pracMap = new Map(practitioners.data?.items.map((p) => [p.id, p.display_name]) ?? []);
  const slotMap = new Map(slots.data?.map((s) => [s.id, s.label]) ?? []);
  const modeMap = new Map(modes.data?.map((m) => [m.id, m.code]) ?? []);

  return (
    <Box>
      <PageHeader
        helpTerm="trameRecurrente"
        title={t("shiftPatterns.title")}
        subtitle={t("shiftPatterns.subtitle")}
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpen(true)}
            disabled={!orgId}
          >
            {t("shiftPatterns.create")}
          </Button>
        }
      />

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 280 }}>
          <InputLabel>{t("nav.organizations")}</InputLabel>
          <Select
            label={t("nav.organizations")}
            value={orgId}
            onChange={(e) => setOrgId(e.target.value)}
          >
            {(orgs.data?.items ?? []).map((o) => (
              <MenuItem key={o.id} value={o.id}>
                {o.code} — {o.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {patterns.isLoading && <CircularProgress />}
      {patterns.data && patterns.data.length === 0 && (
        <Alert severity="info">{t("shiftPatterns.empty")}</Alert>
      )}

      {patterns.data && patterns.data.length > 0 && (
        <Box sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t("shiftPatterns.name")}</TableCell>
              <TableCell>{t("shiftPatterns.practitioner")}</TableCell>
              <TableCell>{t("shiftPatterns.weekday")}</TableCell>
              <TableCell>{t("shiftPatterns.slot")}</TableCell>
              <TableCell>{t("shiftPatterns.mode")}</TableCell>
              <TableCell>{t("shiftPatterns.from")}</TableCell>
              <TableCell>{t("shiftPatterns.to")}</TableCell>
              <TableCell>{t("shiftPatterns.active")}</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {patterns.data.map((p: ShiftPattern) => (
              <TableRow key={p.id}>
                <TableCell>{p.name ?? "—"}</TableCell>
                <TableCell>{pracMap.get(p.practitioner_id) ?? p.practitioner_id}</TableCell>
                <TableCell>
                  <Chip size="small" label={t(`shiftPatterns.weekdayLabel.${p.weekday}`)} />
                </TableCell>
                <TableCell>{slotMap.get(p.slot_template_id) ?? p.slot_template_id}</TableCell>
                <TableCell sx={{ fontFamily: "monospace" }}>
                  {modeMap.get(p.exercise_mode_id) ?? p.exercise_mode_id}
                </TableCell>
                <TableCell sx={{ fontFamily: "monospace" }}>{p.effective_from}</TableCell>
                <TableCell sx={{ fontFamily: "monospace" }}>{p.effective_to ?? "—"}</TableCell>
                <TableCell>{p.is_active ? t("common.yes") : t("common.no")}</TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => {
                      if (confirm(t("shiftPatterns.deleteConfirm"))) {
                        deleteMutation.mutate(p.id);
                      }
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </Box>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t("shiftPatterns.createTitle")}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label={t("shiftPatterns.name")}
              size="small"
              value={name}
              onChange={(e) => setName(e.target.value)}
              inputProps={{ maxLength: 120 }}
            />
            <FormControl size="small">
              <InputLabel>{t("shiftPatterns.practitioner")}</InputLabel>
              <Select
                label={t("shiftPatterns.practitioner")}
                value={practitionerId}
                onChange={(e) => setPractitionerId(e.target.value)}
              >
                {(practitioners.data?.items ?? []).map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.display_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small">
              <InputLabel>{t("shiftPatterns.slot")}</InputLabel>
              <Select
                label={t("shiftPatterns.slot")}
                value={slotId}
                onChange={(e) => setSlotId(e.target.value)}
              >
                {(slots.data ?? []).map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.label} ({s.start_time.slice(0, 5)}–{s.end_time.slice(0, 5)})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small">
              <InputLabel>{t("shiftPatterns.mode")}</InputLabel>
              <Select
                label={t("shiftPatterns.mode")}
                value={modeId}
                onChange={(e) => setModeId(e.target.value)}
              >
                {(modes.data ?? []).map((m) => (
                  <MenuItem key={m.id} value={m.id}>
                    {m.code} — {m.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small">
              <InputLabel>{t("shiftPatterns.weekday")}</InputLabel>
              <Select
                label={t("shiftPatterns.weekday")}
                value={weekday}
                onChange={(e) => setWeekday(Number(e.target.value))}
              >
                {WEEKDAYS.map((d) => (
                  <MenuItem key={d} value={d}>
                    {t(`shiftPatterns.weekdayLabel.${d}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              type="date"
              label={t("shiftPatterns.from")}
              size="small"
              value={effectiveFrom}
              onChange={(e) => setEffectiveFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              type="date"
              label={t("shiftPatterns.to")}
              size="small"
              value={effectiveTo}
              onChange={(e) => setEffectiveTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText={t("shiftPatterns.toHelp")}
            />
            {formError && <Alert severity="error">{formError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
          <Button
            variant="contained"
            onClick={() => createMutation.mutate()}
            disabled={
              createMutation.isPending ||
              !practitionerId ||
              !slotId ||
              !modeId ||
              !effectiveFrom
            }
          >
            {t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
